import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Package, Users, Receipt, ChatText, Plus, Trash, TrendUp, Broadcast, MagicWand, RocketLaunch, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import EnterpriseLeads from "@/components/EnterpriseLeads";
import NewsletterAdmin from "@/components/NewsletterAdmin";
import ShopifyMapping from "@/components/ShopifyMapping";

const TABS = ["overview", "enterprise", "newsletter", "shopify", "radar", "mission", "products", "content", "inquiries", "orders", "users"];

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [content, setContent] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [showProd, setShowProd] = useState(false);
  const [prodForm, setProdForm] = useState({ name: "", description: "", price: "", category: "shop", images: "", stock: 100, featured: false });
  const [radarUrl, setRadarUrl] = useState("");
  const [radarScan, setRadarScan] = useState(null);
  const [radarArt, setRadarArt] = useState(null);
  const [radarBusy, setRadarBusy] = useState(false);
  const [radarGarment, setRadarGarment] = useState("hoodie");
  const [mc, setMc] = useState({ pits_saved_base: 3128, pits_saved_rate: 0.08, kids_reunited_base: 8412, kids_reunited_rate: 0.05 });
  const [mcBusy, setMcBusy] = useState(false);
  const [waitlist, setWaitlist] = useState(0);
  const [blastBusy, setBlastBusy] = useState(false);

  const loadWaitlist = () => api.get("/admin/collab/waitlist").then(({ data }) => setWaitlist(data.count)).catch(() => {});

  const blastWaitlist = async () => {
    if (!window.confirm(`Send the "your collab is ready" email to ${waitlist} people on the waitlist?`)) return;
    setBlastBusy(true);
    try {
      const { data } = await api.post("/admin/collab/blast");
      toast.success(`Blast sent to ${data.sent}/${data.total} on the waitlist 🔥`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBlastBusy(false); }
  };

  const saveMc = async () => {
    setMcBusy(true);
    try {
      await api.put("/admin/mission/counters", {
        pits_saved_base: Number(mc.pits_saved_base), pits_saved_rate: Number(mc.pits_saved_rate),
        kids_reunited_base: Number(mc.kids_reunited_base), kids_reunited_rate: Number(mc.kids_reunited_rate),
      });
      toast.success("Mission ticker updated — climbing live");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setMcBusy(false); }
  };

  const loadAll = () => {
    api.get("/mission/counters").then(({ data }) => setMc({
      pits_saved_base: Math.floor(data.pits_saved), pits_saved_rate: data.pits_saved_rate,
      kids_reunited_base: Math.floor(data.kids_reunited), kids_reunited_rate: data.kids_reunited_rate,
    })).catch(() => {});
    loadWaitlist();
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/content").then(({ data }) => setContent(data)).catch(() => {});
    api.get("/inquiries").then(({ data }) => setInquiries(data)).catch(() => {});
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const createProduct = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", {
        ...prodForm, price: parseFloat(prodForm.price), stock: parseInt(prodForm.stock, 10),
        images: prodForm.images.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Product created");
      setShowProd(false);
      setProdForm({ name: "", description: "", price: "", category: "shop", images: "", stock: 100, featured: false });
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteProduct = async (id) => {
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); loadAll(); }
    catch { toast.error("Delete failed"); }
  };

  const exportShopify = async () => {
    try {
      const res = await api.get("/shopify/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "mr-wigglez-shopify-products.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Shopify CSV downloaded — import it in Shopify → Products → Import 🐾");
    } catch { toast.error("Export failed"); }
  };

  const setInquiryStatus = async (id, status) => {
    try { await api.put(`/inquiries/${id}/status`, null, { params: { status } }); loadAll(); }
    catch { toast.error("Update failed"); }
  };

  const runScan = async () => {
    setRadarBusy(true); setRadarArt(null); setRadarScan(null);
    try {
      const { data } = await api.post("/radar/scan", { url: radarUrl });
      setRadarScan(data);
      toast.success(`Locked onto ${data.brand} · ${data.palette.length} colors matched`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Scan failed"); }
    finally { setRadarBusy(false); }
  };

  const runGenerate = async () => {
    if (!radarScan) return;
    setRadarBusy(true);
    try {
      const { data } = await api.post("/radar/generate", {
        source_url: radarScan.source_url, brand: radarScan.brand, garment: radarGarment, palette: radarScan.palette,
      });
      setRadarArt(data);
      toast.success("Match generated 🔥");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Generation needs Universal Key top-up"); }
    finally { setRadarBusy(false); }
  };

  const pushLive = async () => {
    if (!radarArt) { toast.error("Generate the match first"); return; }
    setRadarBusy(true);
    try {
      await api.post("/radar/publish", {
        name: `Wigglez x ${radarScan.brand} — Radar Drop`,
        price: radarArt.suggested_price || 149,
        image_url: `${api.defaults.baseURL}/collab-image/${radarArt.art_path}`,
        garment: radarGarment, brand: radarScan.brand, palette: radarScan.palette,
        stock: 30, exclusivity: "1 of 30",
      });
      toast.success("Pushed live to the shop 🚀");
      setRadarUrl(""); setRadarScan(null); setRadarArt(null); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Publish failed"); }
    finally { setRadarBusy(false); }
  };

  const STAT_CARDS = stats ? [
    { label: "Revenue", value: `$${stats.revenue.toLocaleString()}`, icon: TrendUp },
    { label: "Orders", value: stats.orders, icon: Receipt },
    { label: "Products", value: stats.products, icon: Package },
    { label: "Users", value: stats.users, icon: Users },
    { label: "Open inquiries", value: stats.open_inquiries, icon: ChatText },
  ] : [];

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="admin-page">
      <p className="overline text-secondary mb-2">Admin</p>
      <h1 className="text-4xl tracking-tighter font-light mb-8">Platform Control</h1>

      <div className="flex gap-2 mb-8 overflow-x-auto no-scrollbar border-b border-white/10">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-3 overline capitalize whitespace-nowrap transition-colors border-b-2 -mb-px ${tab === t ? "text-primary border-primary" : "text-muted-foreground border-transparent hover:text-white"}`}
            data-testid={`admin-tab-${t}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-overview">
          {STAT_CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div key={c.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="h-full p-5 rounded-xl bg-card border border-white/5">
                <Icon weight="duotone" size={22} className="text-primary mb-3" />
                <p className="text-2xl font-modern font-semibold">{c.value}</p>
                <p className="text-xs text-muted-foreground mt-1 overline">{c.label}</p>
              </motion.div>
            );
          })}
        </div>
      )}

      {tab === "enterprise" && <EnterpriseLeads />}

      {tab === "newsletter" && <NewsletterAdmin />}

      {tab === "radar" && (
        <div className="max-w-3xl" data-testid="admin-radar">
          <div className="rounded-2xl border border-primary/30 bg-card p-6 neon-glow">
            <p className="overline text-primary mb-2 inline-flex items-center gap-2"><Broadcast weight="fill" size={15} /> Wigglez Radar</p>
            <h2 className="font-display text-2xl mb-1">Match the biggest drops in the game</h2>
            <p className="text-sm text-muted-foreground mb-5">Paste a link from Melin, Nike or Jordan. We pull its exact palette and generate a 1-of-1 Wigglez match, then push it live as a limited collab drop.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input value={radarUrl} onChange={(e) => setRadarUrl(e.target.value)} placeholder="https://melin.com/products/…"
                className="flex-1 bg-transparent border border-white/15 rounded-full px-5 py-3 outline-none focus:border-primary" data-testid="radar-url" />
              <button onClick={runScan} disabled={radarBusy || !radarUrl} className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-modern font-medium disabled:opacity-50" data-testid="radar-scan-btn">
                <Broadcast weight="fill" size={16} /> {radarBusy ? "Working…" : "Scan drop"}
              </button>
            </div>
          </div>

          {radarScan && (
            <div className="mt-6 rounded-2xl border border-white/10 bg-card p-6" data-testid="radar-result">
              <p className="overline text-secondary mb-4">Locked onto <span className="text-white">{radarScan.brand}</span></p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <p className="overline text-muted-foreground mb-2">Source</p>
                  {radarScan.image_url
                    ? <img src={radarScan.image_url} alt="source" className="w-full rounded-xl border border-white/10 object-cover aspect-square" data-testid="radar-source-img" />
                    : <div className="aspect-square rounded-xl bg-muted grid place-items-center text-xs text-muted-foreground text-center p-4">No preview image found — palette defaulted to Wigglez neon</div>}
                </div>
                <div>
                  <p className="overline text-muted-foreground mb-2">Matched palette</p>
                  <div className="flex flex-wrap gap-2 mb-5" data-testid="radar-palette">
                    {radarScan.palette.map((hex, i) => (
                      <div key={i} className="text-center">
                        <div className="h-12 w-12 rounded-lg border border-white/20" style={{ background: hex }} />
                        <span className="text-[10px] text-muted-foreground">{hex}</span>
                      </div>
                    ))}
                  </div>
                  <p className="overline text-muted-foreground mb-2">Garment</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {["hoodie", "tee", "bandana", "sweatsuit"].map((g) => (
                      <button key={g} onClick={() => setRadarGarment(g)} className={`overline px-3 py-1.5 rounded-full border capitalize ${radarGarment === g ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-muted-foreground hover:text-white"}`} data-testid={`radar-garment-${g}`}>{g}</button>
                    ))}
                  </div>
                  <button onClick={runGenerate} disabled={radarBusy} className="w-full inline-flex items-center justify-center gap-2 border border-secondary/50 text-secondary px-5 py-3 rounded-full font-modern hover:bg-secondary hover:text-black transition-colors disabled:opacity-50" data-testid="radar-generate-btn">
                    <MagicWand weight="fill" size={16} /> {radarBusy ? "Generating…" : "Generate match"}
                  </button>
                </div>
              </div>

              {radarArt && (
                <div className="mt-6 border-t border-white/10 pt-6" data-testid="radar-art">
                  <p className="overline text-primary mb-3">The match — Wigglez x {radarScan.brand}</p>
                  <img src={`${api.defaults.baseURL}/collab-image/${radarArt.art_path}`} alt="match" className="w-full max-w-xs rounded-xl border border-primary/30" />
                </div>
              )}

              <button onClick={pushLive} disabled={radarBusy || !radarArt} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-3.5 rounded-full font-modern font-semibold disabled:opacity-40" data-testid="radar-publish-btn">
                <RocketLaunch weight="fill" size={16} /> Push to Live Shop
              </button>
              <p className="text-xs text-muted-foreground mt-2">Generating the design uses your Universal Key. Palette scan works without it.</p>
            </div>
          )}
        </div>
      )}

      {tab === "products" && (
        <div data-testid="admin-products">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <button onClick={() => setShowProd((s) => !s)} className="inline-flex items-center gap-2 overline px-4 py-2 rounded-full border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground transition-colors" data-testid="admin-add-product">
              <Plus size={16} /> Add product
            </button>
            <button onClick={exportShopify} className="inline-flex items-center gap-2 overline px-4 py-2 rounded-full font-semibold text-black hover:opacity-90 transition-opacity" style={{ background: "#ccff00" }} data-testid="admin-export-shopify">
              <DownloadSimple weight="bold" size={16} /> Export to Shopify (CSV)
            </button>
            <span className="text-xs text-muted-foreground">{products.length} products · Shopify → Products → Import</span>
          </div>
          {showProd && (
            <form onSubmit={createProduct} className="mb-8 p-6 rounded-xl bg-card border border-white/10 grid sm:grid-cols-2 gap-3" data-testid="admin-product-form">
              <input required placeholder="Name" value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} className="bg-transparent border border-white/15 rounded-lg px-4 py-2.5 outline-none focus:border-primary" data-testid="ap-name" />
              <input required placeholder="Price" type="number" step="0.01" value={prodForm.price} onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })} className="bg-transparent border border-white/15 rounded-lg px-4 py-2.5 outline-none focus:border-primary" data-testid="ap-price" />
              <textarea required placeholder="Description" value={prodForm.description} onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} className="sm:col-span-2 bg-transparent border border-white/15 rounded-lg px-4 py-2.5 outline-none focus:border-primary resize-none" rows={2} data-testid="ap-desc" />
              <input placeholder="Image URLs (comma separated)" value={prodForm.images} onChange={(e) => setProdForm({ ...prodForm, images: e.target.value })} className="sm:col-span-2 bg-transparent border border-white/15 rounded-lg px-4 py-2.5 outline-none focus:border-primary" data-testid="ap-images" />
              <select value={prodForm.category} onChange={(e) => setProdForm({ ...prodForm, category: e.target.value })} className="bg-card border border-white/15 rounded-lg px-4 py-2.5 outline-none focus:border-primary" data-testid="ap-category">
                <option value="shop">Shop</option>
                <option value="luxury">Luxury</option>
              </select>
              <label className="flex items-center gap-2 text-sm text-muted-foreground px-2">
                <input type="checkbox" checked={prodForm.featured} onChange={(e) => setProdForm({ ...prodForm, featured: e.target.checked })} data-testid="ap-featured" /> Featured
              </label>
              <button className="sm:col-span-2 bg-primary text-primary-foreground py-2.5 rounded-full font-modern font-medium" data-testid="ap-submit">Create</button>
            </form>
          )}
          <div className="space-y-2">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-3 rounded-lg bg-card border border-white/5" data-testid={`admin-product-${p.id}`}>
                <div className="h-12 w-12 rounded-lg overflow-hidden bg-muted shrink-0">{p.images?.[0] && <img src={p.images[0]} alt="" className="h-full w-full object-cover" />}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-modern font-medium truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.category} · ${p.price} {p.featured && "· Featured"}</p>
                </div>
                <button onClick={() => deleteProduct(p.id)} className="text-muted-foreground hover:text-accent transition-colors" data-testid={`admin-delete-product-${p.id}`}><Trash size={18} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "shopify" && <ShopifyMapping />}

      {tab === "content" && (
        <div className="space-y-2" data-testid="admin-content">
          {content.map((c) => (
            <div key={c.id} className="flex items-center gap-4 p-3 rounded-lg bg-card border border-white/5">
              <div className="h-12 w-12 rounded-lg overflow-hidden bg-muted shrink-0">{c.cover_image && <img src={c.cover_image} alt="" className="h-full w-full object-cover" />}</div>
              <div className="flex-1"><p className="font-modern font-medium">{c.title}</p><p className="text-xs text-muted-foreground">{c.section}</p></div>
            </div>
          ))}
        </div>
      )}

      {tab === "inquiries" && (
        <div className="space-y-3" data-testid="admin-inquiries">
          {inquiries.length === 0 ? <p className="text-muted-foreground py-6">No inquiries yet.</p> :
            inquiries.map((q) => (
              <div key={q.id} className="p-4 rounded-lg bg-card border border-white/5" data-testid={`admin-inquiry-${q.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="overline text-primary">{q.type}</span>
                    <span className="text-xs text-muted-foreground">{q.name} · {q.email}</span>
                  </div>
                  <select value={q.status} onChange={(e) => setInquiryStatus(q.id, e.target.value)} className="bg-card border border-white/15 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary" data-testid={`admin-inquiry-status-${q.id}`}>
                    <option value="new">New</option>
                    <option value="in_progress">In progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                <p className="text-sm text-muted-foreground">{q.message}</p>
              </div>
            ))}
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-3" data-testid="admin-orders">
          {orders.length === 0 ? <p className="text-muted-foreground py-6">No orders yet.</p> :
            orders.map((o) => (
              <div key={o.id} className="p-4 rounded-lg bg-card border border-white/5 flex justify-between items-center">
                <div><p className="font-modern font-medium">{o.user_email}</p><p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()} · {o.items.length} items</p></div>
                <span className="text-primary font-modern">${o.total.toLocaleString()}</span>
              </div>
            ))}
        </div>
      )}

      {tab === "mission" && (
        <div className="max-w-2xl" data-testid="admin-mission">
          <div className="rounded-2xl border border-primary/30 bg-card p-6 neon-glow">
            <p className="overline text-primary mb-2 inline-flex items-center gap-2"><TrendUp weight="fill" size={15} /> Mission Ticker</p>
            <h2 className="font-display text-2xl mb-1">"Every Drop Runs for a Reason"</h2>
            <p className="text-sm text-muted-foreground mb-6">Control the live homepage counters. The base is the number shown right now; the rate is how many climb per second (0.08 ≈ one every ~12s).</p>
            <div className="grid grid-cols-2 gap-5">
              <label className="block"><span className="overline text-muted-foreground">Pits saved · base</span>
                <input type="number" value={mc.pits_saved_base} onChange={(e) => setMc((m) => ({ ...m, pits_saved_base: e.target.value }))}
                  className="w-full mt-1.5 bg-transparent border border-white/15 rounded-[10px] px-4 py-3 outline-none focus:border-primary" data-testid="mc-pits-base" /></label>
              <label className="block"><span className="overline text-muted-foreground">Pits · rate /sec</span>
                <input type="number" step="0.001" value={mc.pits_saved_rate} onChange={(e) => setMc((m) => ({ ...m, pits_saved_rate: e.target.value }))}
                  className="w-full mt-1.5 bg-transparent border border-white/15 rounded-[10px] px-4 py-3 outline-none focus:border-primary" data-testid="mc-pits-rate" /></label>
              <label className="block"><span className="overline text-muted-foreground">Kids reunited · base</span>
                <input type="number" value={mc.kids_reunited_base} onChange={(e) => setMc((m) => ({ ...m, kids_reunited_base: e.target.value }))}
                  className="w-full mt-1.5 bg-transparent border border-white/15 rounded-[10px] px-4 py-3 outline-none focus:border-primary" data-testid="mc-kids-base" /></label>
              <label className="block"><span className="overline text-muted-foreground">Kids · rate /sec</span>
                <input type="number" step="0.001" value={mc.kids_reunited_rate} onChange={(e) => setMc((m) => ({ ...m, kids_reunited_rate: e.target.value }))}
                  className="w-full mt-1.5 bg-transparent border border-white/15 rounded-[10px] px-4 py-3 outline-none focus:border-primary" data-testid="mc-kids-rate" /></label>
            </div>
            <button onClick={saveMc} disabled={mcBusy} className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-[10px] font-modern font-medium disabled:opacity-50" data-testid="mc-save">
              <TrendUp weight="fill" size={16} /> {mcBusy ? "Saving…" : "Update live ticker"}
            </button>
          </div>

          <div className="rounded-2xl border border-secondary/30 bg-card p-6 mt-6" data-testid="admin-collab-waitlist">
            <p className="overline text-secondary mb-2 inline-flex items-center gap-2"><MagicWand weight="fill" size={15} /> 1-of-1 Collab Waitlist</p>
            <h2 className="font-display text-2xl mb-1">Blast the waitlist when AI is back</h2>
            <p className="text-sm text-muted-foreground mb-5">
              <span className="font-modern text-white text-lg">{waitlist}</span> {waitlist === 1 ? "person is" : "people are"} waiting for the AI studio to reopen. Sends everyone the "your 1-of-1 collab is ready" email via Resend.
            </p>
            <button onClick={blastWaitlist} disabled={blastBusy || waitlist === 0}
              className="inline-flex items-center gap-2 bg-secondary text-black px-6 py-3 rounded-[10px] font-modern font-medium disabled:opacity-40" data-testid="collab-blast">
              <RocketLaunch weight="fill" size={16} /> {blastBusy ? "Sending…" : `Blast ${waitlist} on the waitlist`}
            </button>
          </div>
        </div>
      )}

      {tab === "users" && (
        <div className="space-y-2" data-testid="admin-users">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-4 p-3 rounded-lg bg-card border border-white/5">
              <div className="h-9 w-9 rounded-full bg-primary/20 grid place-items-center text-primary font-modern font-semibold">{(u.name || "?")[0]}</div>
              <div className="flex-1"><p className="font-modern font-medium">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p></div>
              <span className={`overline px-2.5 py-1 rounded-full ${u.role === "admin" ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground"}`}>{u.role}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
